import React, { useEffect, useState, useRef } from 'react';
import { ChatMessage } from './components/ChatMessage';
import { ChatInput } from './components/ChatInput';
import { Message, BookingState, ConversationStage } from './types/booking';
import { getBotResponse } from './utils/chatbot';
import { SparklesIcon } from 'lucide-react';
export function App() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [stage, setStage] = useState<ConversationStage>('service');
  const [bookingState, setBookingState] = useState<BookingState>({});
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const initialMessage: Message = {
      id: '1',
      text: "Welcome to Beautii! I'm here to help you book your perfect treatment. What service are you interested in today?",
      sender: 'bot',
      timestamp: new Date(),
      options: ['Swedish Massage', 'Deep Tissue Massage', 'Hot Stone Massage', 'Facial Treatment', 'Manicure', 'Pedicure']
    };
    setMessages([initialMessage]);
  }, []);
  useEffect(() => {
    // Only scroll within the chat container, not the entire page
    if (messagesEndRef.current && chatContainerRef.current) {
      const container = chatContainerRef.current;
      const element = messagesEndRef.current;
      // Scroll only within the container, don't affect parent page
      container.scrollTop = element.offsetTop;
    }
  }, [messages]);
  const handleSendMessage = (text: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      text,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);
    let updatedBookingState = {
      ...bookingState
    };
    switch (stage) {
      case 'service':
        updatedBookingState.service = text;
        break;
      case 'location':
        updatedBookingState.location = text;
        break;
      case 'date':
        updatedBookingState.date = text;
        break;
      case 'therapist':
        const [therapist, time] = text.split(' - ');
        updatedBookingState.therapist = therapist;
        updatedBookingState.time = time;
        break;
    }
    setBookingState(updatedBookingState);
    setTimeout(() => {
      const response = getBotResponse(stage, text, updatedBookingState);
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: response.message,
        sender: 'bot',
        timestamp: new Date(),
        options: response.options,
        therapists: response.therapists
      };
      setMessages(prev => [...prev, botMessage]);
      setStage(response.nextStage);
      setIsTyping(false);
    }, 1000);
  };
  const handleOptionClick = (option: string) => {
    handleSendMessage(option);
  };
  return <div className="flex flex-col h-screen w-full bg-gradient-to-br from-purple-50 to-pink-50" data-id="element-0">
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm" data-id="element-1">
        <div className="flex items-center gap-3" data-id="element-2">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center" data-id="element-3">
            <SparklesIcon className="w-5 h-5 text-white" data-id="element-4" />
          </div>
          <div data-id="element-5">
            <h1 className="text-xl font-bold text-gray-800" data-id="element-6">
              Beautii Booking Assistant
            </h1>
            <p className="text-sm text-gray-500" data-id="element-7">
              Book your perfect treatment in seconds
            </p>
          </div>
        </div>
      </div>
      <div ref={chatContainerRef} className="flex-1 overflow-y-auto px-6 py-6" data-id="element-8">
        <div className="max-w-3xl mx-auto" data-id="element-9">
          {messages.map(message => <ChatMessage key={message.id} message={message} onOptionClick={handleOptionClick} data-id="element-10" />)}
          {isTyping && <div className="flex justify-start mb-4" data-id="element-11">
              <div className="bg-gray-100 rounded-2xl px-4 py-3" data-id="element-12">
                <div className="flex gap-1" data-id="element-13">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" data-id="element-14" />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                animationDelay: '0.1s'
              }} data-id="element-15" />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                animationDelay: '0.2s'
              }} data-id="element-16" />
                </div>
              </div>
            </div>}
          <div ref={messagesEndRef} data-id="element-17" />
        </div>
      </div>
      <div className="bg-white" data-id="element-18">
        <div className="max-w-3xl mx-auto" data-id="element-19">
          <ChatInput onSend={handleSendMessage} disabled={isTyping} data-id="element-20" />
        </div>
      </div>
    </div>;
}